import { logger } from "../utils/logger";
import React, { useEffect, useRef, useState } from 'react';
import { consumeOAuthReturn, oauthSuccessPath } from '../utils/oauthReturn';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useSnackbar } from 'notistack';
import { api } from '../services/api';
import { importQuickBooksTransactions } from '../utils/quickbooksImport';
import { useAuth } from '../contexts/AuthContext';
import { mapErrorToUserMessage } from '../utils/errorMapping';

const QuickBooksCallback: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [error, setError] = useState<string | null>(null);
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState<string>('');
    const { user, refreshUser } = useAuth();
    const [loginPrompt, setLoginPrompt] = useState(false);
    const [restoring, setRestoring] = useState(false);

    const handledRef = useRef(false);

    useEffect(() => {
        // Run exactly once: the auth code is single-use, and re-running when the
        // auth context hydrates used to fire a second exchange (and error out).
        if (handledRef.current) return;
        handledRef.current = true;
        const handleCallback = async () => {
            const returnInfo = consumeOAuthReturn('quickbooks');
            try {
                const code = searchParams.get('code');
                const realmId = searchParams.get('realmId');
                const state = searchParams.get('state');
                const oauthError = searchParams.get('error');
                if (oauthError) {
                    setError(oauthError === 'access_denied'
                        ? 'QuickBooks connection was cancelled.'
                        : 'QuickBooks did not authorize the connection. Please try again.');
                    return;
                }
                if (!code || !realmId || !state) {
                    logger.error('[QuickBooksCallback] Missing required OAuth parameters');
                    setError('Missing required OAuth parameters. Please try connecting again.');
                    return;
                }
                // The callback endpoint is authenticated by the one-time OAuth state, not the
                // session, so a stale/expired login can never log the user out here.
                const response = await api.post('/api/v1/platforms/quickbooks/oauth/callback', {
                    code,
                    realmId,
                    state
                });
                const ok = response.data?.success === true || response.data?.success === 'true'
                    || response.data?.message === 'Request is being processed';
                if (ok) {
                    enqueueSnackbar('QuickBooks connected successfully!', { variant: 'success' });
                    if (user) {
                        refreshUser?.().catch(() => undefined);
                    }
                    const dest = oauthSuccessPath('quickbooks', returnInfo, '/complete-tax-view?quickbooks=connected');
                    const returnOrigin = response.data?.return_origin;
                    if (['https://gotrutax.com', 'https://www.gotrutax.com'].includes(returnOrigin)
                        && returnOrigin !== window.location.origin) {
                        window.location.replace(`${returnOrigin}${dest}`);
                        return;
                    }
                    setTimeout(() => navigate(dest, { replace: true }), 800);
                } else {
                    setError(response.data?.message || 'Failed to complete QuickBooks connection');
                }
            } catch (err: any) {
                logger.error('[QuickBooksCallback] Callback failed with status:', err?.response?.status);
                setError(mapErrorToUserMessage(err));
            }
        };
        handleCallback();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loginPrompt) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="50vh"
                p={3}
            >
                <Alert severity="success" sx={{ mb: 2 }}>
                    QuickBooks connected! Please restore your session to continue.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Your QuickBooks account is now connected. Click below to go to your integrations.
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <button
                        onClick={() => navigate('/platforms/integrations')}
                        style={{ padding: '8px 16px', fontSize: '1rem', borderRadius: 4, background: '#1976d2', color: '#fff', border: 'none', cursor: 'pointer' }}
                    >
                        Go to Integrations
                    </button>
                </Box>
                <Typography
                    variant="body2"
                    color="primary"
                    sx={{ cursor: 'pointer', mb: 2 }}
                    onClick={() => navigate('/platforms/integrations')}
                >
                    Go to Integrations
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="50vh"
                p={3}
            >
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Please try connecting to QuickBooks again.
                </Typography>
                <Typography
                    variant="body2"
                    color="primary"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate('/platforms/integrations')}
                >
                    Return to Integrations
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="50vh"
            p={3}
        >
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
                {importing ? 'Importing from QuickBooks...' : 'Connecting to QuickBooks...'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {importing ? importProgress : 'Please wait while we complete your QuickBooks connection.'}
            </Typography>
        </Box>
    );
};

export default QuickBooksCallback;
